import type { ProjectState } from "hooks/useProjects/useProjects.types";

/** A project as the switcher lists it. */
export interface SwitcherProject {
  projectId: string;
  name: string;
  /** Short license label, or null when the list does not say. */
  license: string | null;
  /**
   * When the project was last changed (`updated_at`). The backend has no
   * per-user "last opened" time, so this is the closest real signal.
   */
  updatedAt: string | null;
}

/** The fields of a `GET /projects` row the switcher reads. */
export interface ProjectListRowPayload {
  project_id: string | number;
  name: string;
  updated_at?: string | null;
  created_at?: string | null;
  archived?: boolean;
  is_demo?: boolean;
  purchase_type?: string | null;
  trial_expiry_date?: string | null;
  one_time_purchase?: { product?: { tier?: string | null } | null } | null;
  permissions?: { projectInfo?: { isLicensed?: boolean; isTrialProject?: boolean } } | null;
}

/** License label from a `GET /projects` row. */
export function getListRowLicenseLabel(row: ProjectListRowPayload): string | null {
  if (row.is_demo) return "Demo";
  const info = row.permissions?.projectInfo;
  if (info?.isTrialProject) return "Trial";
  if (info?.isLicensed === false) return "Unlicensed";
  const tier = row.one_time_purchase?.product?.tier;
  if (tier === "studio") return "Studio";
  if (tier === "writer") return "Writer";
  if (row.purchase_type === "subscription") return "Workspace plan";
  return null;
}

/** License label for the open project, from its detail payload. */
export function getProjectLicenseLabel(
  project: Pick<
    ProjectState,
    "isDemo" | "isTrial" | "isLicensed" | "otpSeating" | "aiCredits" | "purchaseType"
  >
): string | null {
  if (project.isDemo) return "Demo";
  if (project.isLicensed === false) return project.isTrial ? "Trial" : "Unlicensed";
  const tier = project.otpSeating?.licenseTier;
  if (tier === "studio") return "Studio";
  if (tier === "writer") return "Writer";
  if (
    project.aiCredits?.walletKind === "workspace_subscription" ||
    project.purchaseType === "subscription"
  ) {
    return "Workspace plan";
  }
  return null;
}

export function toSwitcherProject(row: ProjectListRowPayload): SwitcherProject {
  return {
    projectId: String(row.project_id),
    name: row.name,
    license: getListRowLicenseLabel(row),
    updatedAt: row.updated_at ?? row.created_at ?? null,
  };
}

/**
 * The catalog lists every project of the workspace; `GET /projects` adds
 * licenses but returns one page (at most 100). Merge the page into the
 * catalog so projects past the first page stay searchable. Projects only the
 * list knows (catalog not refreshed yet) are added at the end.
 */
export function mergeSwitcherProjects(
  catalogProjects: readonly SwitcherProject[],
  listProjects: readonly SwitcherProject[] | undefined
): SwitcherProject[] {
  if (!listProjects?.length) return [...catalogProjects];
  const fromList = new Map(listProjects.map((project) => [project.projectId, project]));
  const merged = catalogProjects.map((project) => {
    const listed = fromList.get(project.projectId);
    if (!listed) return project;
    fromList.delete(project.projectId);
    return {
      ...project,
      name: listed.name || project.name,
      license: listed.license ?? project.license,
      updatedAt: listed.updatedAt ?? project.updatedAt,
    };
  });
  return [...merged, ...fromList.values()];
}

/** Most recently changed first; projects without a date go last, by name. */
export function sortRecentProjects(projects: readonly SwitcherProject[]): SwitcherProject[] {
  return [...projects].sort((a, b) => {
    const aTime = a.updatedAt ? Date.parse(a.updatedAt) : Number.NaN;
    const bTime = b.updatedAt ? Date.parse(b.updatedAt) : Number.NaN;
    const aValid = Number.isFinite(aTime);
    const bValid = Number.isFinite(bTime);
    if (aValid && bValid && aTime !== bTime) return bTime - aTime;
    if (aValid !== bValid) return aValid ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

/** Case-insensitive name match. An empty query matches everything. */
export function filterProjectsByQuery(
  projects: readonly SwitcherProject[],
  query: string
): SwitcherProject[] {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return [...projects];
  return projects.filter((project) => project.name.toLocaleLowerCase().includes(needle));
}

/** "Today", "Yesterday", or a short date like "Oct 12" ("Oct 12, 2025" in another year). */
export function formatRecentDate(isoDate: string | null, now: Date = new Date()): string | null {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return null;
  const startOfDay = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (dayDiff === 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

/** "Updated today", "Updated yesterday" or "Updated Oct 12". */
export function formatUpdatedLabel(isoDate: string | null, now: Date = new Date()): string | null {
  const date = formatRecentDate(isoDate, now);
  if (!date) return null;
  const relative = date === "Today" || date === "Yesterday";
  return `Updated ${relative ? date.toLowerCase() : date}`;
}

export interface ProjectDraft {
  documentId: string;
  /** "Draft 1" is the first version uploaded. */
  label: string;
  createdAt: string;
}

/** Numbers versions in upload order and lists the newest first. */
export function toProjectDrafts(
  versions: ReadonlyArray<{ documentId: string; createdAt: string }>
): ProjectDraft[] {
  const oldestFirst = [...versions].sort(
    (a, b) => (Date.parse(a.createdAt) || 0) - (Date.parse(b.createdAt) || 0)
  );
  return oldestFirst
    .map((version, index) => ({
      documentId: String(version.documentId),
      label: `Draft ${index + 1}`,
      createdAt: version.createdAt,
    }))
    .reverse();
}
